import type { Locale } from "@/lib/i18n/config";
import type { Dictionary } from "@/lib/i18n/get-dictionary";
import type {
	NavGroup as ResolvedNavGroup,
	NavLink as ResolvedNavLink,
} from "./nav-links";

export type NavSection = "explore" | "business" | "data";

export type NavItem = {
	key: string;
	/** Path appended to `/${locale}` — every entry here is a static top-level
	 *  route, never a job/apply URL (see src/lib/sitemap/CLAUDE.md for those). */
	href: string;
	/** Dictionary key (navbar namespace) for the visible label. */
	labelKey: string;
	/** Dictionary key for the one-line "what you'll find here" blurb shown by
	 *  NavDropdown on desktop. Optional — the mobile drawer never renders it. */
	descriptionKey?: string;
	/** Reserved for a future desktop/mobile divergence. Nothing sets this today:
	 *  the 2026-08-10 audit found /advertise and /blog mobile-only with no
	 *  desktop equivalent (orphan destinations) — the PR-2 remediation promoted
	 *  both to every surface instead of gating them here. */
	mobileOnly?: boolean;
	/** Promotes this item to a standalone top-level link on desktop instead of
	 *  nesting it inside its group's dropdown (see `resolveTopLevelLinks`).
	 *  Mobile ignores this — the drawer has no "top-level" concept, so the
	 *  item still renders under its section heading there. PR-3 (2026-08-10,
	 *  zero-risk fallback IA): Lowongan/jobs was buried as item 1 of the
	 *  Jelajahi dropdown despite being the single highest-intent destination
	 *  — census: 5.1 dropdown opens per 100 pageviews, so most visitors never
	 *  saw it required a click just to open the menu. */
	topLevel?: boolean;
	/** `href` is used as-is, never locale-prefixed: for a path another zone
	 *  serves outside the `/${locale}` tree (e.g. `/play/...`). */
	absolute?: boolean;
	/** PostHog flag that is this item's SOLE switch. The item resolves only when
	 *  the caller passes the flag in `enabledFlags`; the caller evaluates it
	 *  (navbar.tsx, server-side, without cookies/headers). */
	flag?: string;
	section: NavSection;
};

export type NavGroup = {
	key: NavSection;
	labelKey: string;
	/** Caps how many items resolve into this group's dropdown, on BOTH
	 *  desktop and mobile (mobile benefits too — fewer visible drawer links
	 *  for the PR-3 Task 3.3 guest-link-count target). Anything cut still
	 *  stays reachable via the /insights hub (which links every content axis
	 *  — reports/dataset/research/digest) and the footer (see footer.tsx). */
	dropdownLimit?: number;
	items: NavItem[];
};

// Single source of truth for site navigation — desktop (navbar.tsx, via
// NavDropdown) and mobile (mobile-nav-menu.tsx) both map over this instead of
// keeping their own hand-written lists. The 2026-08-05 sitewide /research
// omission (a destination missing from every nav surface because it only
// lived in one of two hand-maintained arrays) is the drift class this exists
// to close off.
export const NAV_CATALOG: NavGroup[] = [
	{
		key: "explore",
		labelKey: "navExplore",
		items: [
			{
				key: "jobs",
				href: "/jobs",
				labelKey: "jobs",
				descriptionKey: "jobsDesc",
				topLevel: true,
				section: "explore",
			},
			// 2026-10-10: Hunt / AI apply had no nav entry at all, so the only ways
			// in were the homepage hero and a job-page button most visitors never
			// see (PostHog 30d: /extension 0 pageviews, AI Apply badge 1% CTR).
			// Top-level so it is one click from every page. Logged-out /seeker
			// renders Hunt (no login wall before value).
			{
				key: "ai-apply",
				href: "/seeker",
				labelKey: "aiApply",
				descriptionKey: "aiApplyDesc",
				topLevel: true,
				section: "explore",
			},
			// 2026-10-31 owner: "showing the 3D fair to everyone". The 3D fair is
			// the simulator zone; bare /play/fair 302s to the live/next open event
			// (apps/simulator/worker/routes/fair.ts), so the navbar, which renders
			// on ISR pages, never needs a per-request event lookup. Gated by the
			// `job-fair` flag, its sole switch.
			{
				key: "job-fair-3d",
				href: "/play/fair",
				labelKey: "fairWorld",
				descriptionKey: "fairWorldDesc",
				absolute: true,
				flag: "job-fair",
				section: "explore",
			},
			{
				key: "companies",
				href: "/companies",
				labelKey: "companies",
				descriptionKey: "companiesDesc",
				section: "explore",
			},
			{
				key: "salary-guide",
				href: "/salary-guide",
				labelKey: "salaryGuide",
				descriptionKey: "salaryGuideDesc",
				section: "explore",
			},
			{
				key: "cek-ats-cv",
				href: "/tools/cek-ats-cv",
				labelKey: "cekAts",
				descriptionKey: "cekAtsDesc",
				section: "explore",
			},
			{
				key: "opportunities",
				href: "/opportunities",
				labelKey: "opportunities",
				descriptionKey: "opportunitiesDesc",
				section: "explore",
			},
			// Moved out of `business` on 2026-08-26. /pricing is the SEEKER money
			// page — it renders a `#seekers` / "Pencari Kerja" plan grid, and the
			// measured payer archetypes both convert on it (PRODUCT.md). Filing it
			// under "Untuk Bisnis" meant the one surface a paying seeker needs was
			// reachable only by opening a menu addressed to employers. Business
			// visitors keep their path: /employers links pricing in-page, and the
			// footer carries it on every surface.
			{
				key: "pricing",
				href: "/pricing",
				labelKey: "pricing",
				descriptionKey: "pricingDesc",
				section: "explore",
			},
		],
	},
	{
		key: "business",
		labelKey: "navBusiness",
		items: [
			{
				// B2B hub consolidation (2026-09-27): was one of three entries all
				// pointing at `/employers` in different framings
				// (business-overview/hire-ai-employees/post-a-job); the page itself
				// consolidated to one hub, so the nav does too — a single "For
				// business" entry into it. `/ai-employees` stays live and
				// instrumented (source plan), just out of nav.
				key: "business-overview",
				href: "/employers",
				labelKey: "businessOverview",
				descriptionKey: "businessOverviewDesc",
				section: "business",
			},
			{
				// Owner 2026-11-02: the free employer tools (AI recruiter,
				// assessment, CV screening, ...) were linked only from each other
				// and had 2 human visits in 60 days. Shipped = linked.
				key: "hiring-tools",
				href: "/tools#employer-tools",
				labelKey: "hiringTools",
				descriptionKey: "hiringToolsDesc",
				section: "business",
			},
			{
				key: "hire-talent",
				href: "/talent",
				labelKey: "hireTalent",
				descriptionKey: "hireTalentDesc",
				section: "business",
			},
			{
				key: "collaboration",
				href: "/collaboration",
				labelKey: "collaborations",
				descriptionKey: "collaborationsDesc",
				section: "business",
			},
			// PR-2 (2026-08-10): was mobile-only (mobile-nav-menu.tsx businessItems)
			// with no desktop equivalent — an orphan destination. Judgment call per
			// the plan's stated default: promote to desktop too, appended last so
			// existing item order/positions don't shift. Flag for Kelvin: if he'd
			// rather keep this mobile-only, set `mobileOnly: true` here instead.
			{
				key: "advertise",
				href: "/advertise",
				labelKey: "advertise",
				descriptionKey: "advertiseDesc",
				section: "business",
			},
		],
	},
	{
		key: "data",
		labelKey: "navData",
		// PR-3 (2026-08-10, zero-risk fallback IA): merged from 6 items to 2 —
		// insights (the hub that links reports/dataset/research/digest) plus
		// reports (the highest-value standalone destination). The other 4 stay
		// reachable via the insights hub and the footer.
		dropdownLimit: 2,
		items: [
			{
				key: "insights",
				href: "/insights",
				labelKey: "insights",
				descriptionKey: "insightsDesc",
				section: "data",
			},
			{
				key: "reports",
				href: "/reports",
				labelKey: "reports",
				descriptionKey: "reportsDesc",
				section: "data",
			},
			{
				key: "dataset",
				href: "/data",
				labelKey: "dataset",
				descriptionKey: "datasetDesc",
				section: "data",
			},
			{
				// 2026-08-05: /research had no navbar or footer entry anywhere on the
				// site despite being the surface that carries the methodology (and
				// therefore the citability) behind every report and dataset.
				key: "research",
				href: "/research",
				labelKey: "research",
				descriptionKey: "researchDesc",
				section: "data",
			},
			{
				key: "digest",
				href: "/digest",
				labelKey: "digest",
				descriptionKey: "digestDesc",
				section: "data",
			},
			// PR-2 (2026-08-10): same orphan-destination fix as /advertise above —
			// was mobile-only (mobile-nav-menu.tsx dataItems), promoted to desktop.
			{
				key: "blog",
				href: "/blog",
				labelKey: "blog",
				descriptionKey: "blogDesc",
				section: "data",
			},
		],
	},
];

/** Every PostHog flag some catalog item is gated by — what navbar.tsx reads. */
export const NAV_FLAGS: readonly string[] = [
	...new Set(
		NAV_CATALOG.flatMap((group) =>
			group.items.flatMap((item) => (item.flag ? [item.flag] : [])),
		),
	),
];

type ResolveOptions = {
	/** Desktop (NavDropdown) shows the one-line blurb; the mobile drawer never
	 *  does — pass false there so unused strings aren't resolved for nothing. */
	includeDescriptions: boolean;
	/** Mobile shows every item regardless of `mobileOnly`; desktop excludes
	 *  any item explicitly flagged `mobileOnly: true`. */
	forMobile: boolean;
	/** Flags evaluated ON for this render. An item with `flag` resolves only
	 *  when its flag is listed here; absent = every flagged item hidden. */
	enabledFlags?: readonly string[];
};

function isFlagOn(item: NavItem, enabledFlags?: readonly string[]): boolean {
	return !item.flag || (enabledFlags?.includes(item.flag) ?? false);
}

function itemHref(item: NavItem, locale: Locale): string {
	return item.absolute ? item.href : `/${locale}${item.href}`;
}

/**
 * Resolve the catalog into locale-prefixed, dictionary-backed nav groups.
 * Both navbar.tsx (desktop dropdowns) and mobile-nav-menu.tsx (drawer
 * sections) call this instead of keeping their own inline link arrays.
 */
export function resolveNavGroups(
	t: Dictionary,
	locale: Locale,
	{ includeDescriptions, forMobile, enabledFlags }: ResolveOptions,
): ResolvedNavGroup[] {
	return NAV_CATALOG.map((group) => ({
		key: group.key,
		label: t[group.labelKey] ?? group.labelKey,
		items: group.items
			.filter((item) => isFlagOn(item, enabledFlags))
			.filter((item) => forMobile || !item.mobileOnly)
			// `topLevel` items are pulled out into resolveTopLevelLinks below on
			// desktop — mobile keeps them in their section (no top-level concept
			// in a drawer).
			.filter((item) => forMobile || !item.topLevel)
			.slice(0, group.dropdownLimit ?? Number.POSITIVE_INFINITY)
			.map((item): ResolvedNavLink => {
				const link: ResolvedNavLink = {
					href: itemHref(item, locale),
					label: t[item.labelKey] ?? item.key,
				};
				if (includeDescriptions && item.descriptionKey) {
					link.description = t[item.descriptionKey];
				}
				return link;
			}),
	}));
}

/**
 * Resolve every `topLevel: true` catalog item into a standalone link,
 * locale-prefixed and dictionary-backed like resolveNavGroups. Desktop-only
 * concept — navbar.tsx renders these before the dropdown groups so they're
 * reachable in one click from anywhere, without a menu open. The mobile
 * drawer never calls this; each item already appears under its section via
 * resolveNavGroups(..., { forMobile: true }).
 */
export function resolveTopLevelLinks(
	t: Dictionary,
	locale: Locale,
	enabledFlags?: readonly string[],
): ResolvedNavLink[] {
	return NAV_CATALOG.flatMap((group) =>
		group.items
			.filter((item) => item.topLevel && isFlagOn(item, enabledFlags))
			.map(
				(item): ResolvedNavLink => ({
					href: itemHref(item, locale),
					label: t[item.labelKey] ?? item.key,
				}),
			),
	);
}
